/**
 * Editing a submitted nomination — who may, and what the edit sends.
 *
 * Mirrors AWSECEventHub NominationService.update (UpdateNomination, CR-319057061):
 *
 *   who     only the nomination's creator — exact `actor.equals(createdBy)`, with no marketer,
 *           admin or manager exception (an admin acting as the creator passes, as the service
 *           allows). Same comparison as utils/attendance.
 *   which   any status: PENDING_REVIEW, APPROVED, and REJECTED (a resubmit).
 *   when    only while the event takes nominations — PUBLISHED, bdNominationSupported, inside its
 *           window (utils/nominatableEvents `isNominatable`). The service's check is
 *           `requireOpenForNomination`, shared with create, and it does NOT include create's
 *           BU rule: a BD whose BU has moved since they nominated may still edit. So
 *           `isNominatable` is called here without `bdBusinessUnits`.
 *   effect  an edit that changes anything goes back to PENDING_REVIEW and clears the decision
 *           (decidedBy / decidedAt / rejectReason) and the attendance confirmation; one that
 *           changes nothing is a no-op that keeps the status.
 *
 * The patch is PATCH-shaped: a present `accountInformation` / `guestInformation` REPLACES the
 * stored one whole (NominationMapper accountToDo / guestToDo). So an object that changed is sent
 * complete — including the guest fields the form does not show (`jobRole`), which would otherwise
 * be wiped — and an object that did not change is left out, so an edit to the phone alone does not
 * rewrite the guest. The service compares each object field by field (the DOs' equals), so the
 * comparison here is the same one, after the form's own normalisation (trimmed, '' as absent).
 *
 * Here rather than in the page because the coverage config measures only `.ts`.
 */

import type {
  Event,
  Nomination,
  UpdateNominationInput,
} from '../client/EventCalendarClient';
import { compactPhone } from './bdProfile';
import { isNominatable } from './nominatableEvents';
import { editNominationHref } from './nominationLink';

type AccountInformation = NonNullable<Nomination['accountInformation']>;
type GuestInformation = NonNullable<Nomination['guestInformation']>;

/** Why an edit is not offered; undefined when it is. */
export type EditBlock = 'not-creator' | 'event-closed';

/**
 * Why `login` may not edit this nomination now, or undefined if they may.
 *
 * `event` is the nomination's event as the caller sees it; absent (not loaded, or gone) reads as
 * closed — the service 404s an edit whose event no longer exists.
 */
export function editBlock(
  nomination: Nomination,
  event: Event | undefined,
  login: string | undefined,
  now: Date,
): EditBlock | undefined {
  if (!login || !nomination.createdBy || nomination.createdBy !== login) return 'not-creator';
  if (!event || !isNominatable(event, now)) return 'event-closed';
  return undefined;
}

/** Whether `login` may edit this nomination now — the Edit buttons' gate. */
export function canEditNomination(
  nomination: Nomination,
  event: Event | undefined,
  login: string | undefined,
  now: Date,
): boolean {
  return editBlock(nomination, event, login, now) === undefined;
}

/**
 * The edit page's link for this nomination when `login` may edit it now, else undefined — what
 * the Edit buttons (NominationDetail, the list's Actions) take, so each surface asks one question.
 */
export function editHrefFor(
  nomination: Nomination,
  event: Event | undefined,
  login: string | undefined,
  now: Date,
): string | undefined {
  return nomination.nominationId && canEditNomination(nomination, event, login, now)
    ? editNominationHref(nomination.nominationId)
    : undefined;
}

/**
 * The stored answers, as the form holds them: one per question, in question order, '' for none.
 *
 * `formResponses` is written one entry per question of the event's form, in order
 * (NominationService buildFormResponses), and the form is locked once the event is published —
 * so position `i` of the responses is question `i`. Padded or cut to the question count, so the
 * form's positional array is never sparse.
 */
export function answersFromResponses(
  questionCount: number,
  responses: Nomination['formResponses'],
): string[] {
  return Array.from({ length: questionCount }, (_, i) => responses?.[i]?.answer ?? '');
}

/** What the edit form holds, already shaped for the wire (see CreateNominationPage). */
export interface NominationEdits {
  accountInformation: AccountInformation;
  /** The form's guest fields, contact ref included. Fields the form does not show are kept. */
  guestInformation: GuestInformation;
  bdCellPhone: string;
  /** One per question of the event's form, in order; undefined when the event asks nothing. */
  formAnswers?: string[];
}

/** Guest fields the form has no control for: carried over from the stored guest unchanged. */
const KEPT_GUEST_FIELDS = ['jobRole'] as const;

/** '' and whitespace read as absent, as the form submits them. */
function text(value: string | undefined): string | undefined {
  const t = value?.trim();
  return t ? t : undefined;
}

/** An object with its absent fields dropped, so `{ a: undefined }` and `{}` compare equal. */
function compact<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== undefined),
  ) as T;
}

function normAccount(a: AccountInformation | undefined): AccountInformation {
  return compact({
    companyName: text(a?.companyName),
    sfdcAccountId: text(a?.sfdcAccountId),
    accountTags: a?.accountTags?.length ? [...a.accountTags] : undefined,
  });
}

function normGuest(g: GuestInformation | undefined): GuestInformation {
  return compact({
    firstName: text(g?.firstName),
    lastName: text(g?.lastName),
    jobRole: text(g?.jobRole),
    jobTitle: text(g?.jobTitle),
    email: text(g?.email),
    phoneNumber: text(g?.phoneNumber),
    objectId: text(g?.objectId),
    objectType: text(g?.objectType),
    businessUnit: text(g?.businessUnit),
  });
}

function sameFields(a: object, b: object): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => {
    const va = (a as Record<string, unknown>)[k];
    const vb = (b as Record<string, unknown>)[k];
    if (Array.isArray(va) && Array.isArray(vb)) {
      // Ordered, as the service's List.equals is.
      return va.length === vb.length && va.every((x, i) => x === vb[i]);
    }
    return va === vb;
  });
}

/**
 * The UpdateNomination request for `edits`, or null when nothing changed (Save stays disabled,
 * rather than sending a no-op the service would accept and ignore).
 *
 * Each top-level field is present only when it differs from `original`:
 *
 *   accountInformation / guestInformation   sent whole (the service replaces them whole); the
 *                                           guest keeps the stored fields the form cannot show
 *   bdCellPhone                             compared as the service stores it (separators out)
 *   formAnswers                             compared answer by answer; the stored responses
 *                                           are read as answersFromResponses reads them, so a
 *                                           nomination saved before its event had questions
 *                                           and a form left all blank are not a change
 */
export function buildNominationPatch(
  original: Nomination,
  edits: NominationEdits,
): UpdateNominationInput | null {
  const patch: UpdateNominationInput = { nominationId: original.nominationId };
  let changed = false;

  const account = normAccount(edits.accountInformation);
  if (!sameFields(account, normAccount(original.accountInformation))) {
    patch.accountInformation = account;
    changed = true;
  }

  const kept = Object.fromEntries(
    KEPT_GUEST_FIELDS.map((k) => [k, original.guestInformation?.[k]]),
  );
  const guest = normGuest({ ...kept, ...compact(edits.guestInformation) });
  if (!sameFields(guest, normGuest(original.guestInformation))) {
    patch.guestInformation = guest;
    changed = true;
  }

  const phone = compactPhone(edits.bdCellPhone);
  if (phone !== compactPhone(original.bdCellPhone ?? '')) {
    patch.bdCellPhone = edits.bdCellPhone.trim();
    changed = true;
  }

  if (edits.formAnswers) {
    const before = answersFromResponses(edits.formAnswers.length, original.formResponses);
    if (edits.formAnswers.some((a, i) => a !== before[i])) {
      patch.formAnswers = [...edits.formAnswers];
      changed = true;
    }
  }

  return changed ? patch : null;
}

/**
 * What saving will undo, for the confirmation before an edit is sent — empty when nothing is
 * lost (a PENDING_REVIEW nomination just stays in review).
 */
export function editConsequences(nomination: Nomination): string[] {
  const lost: string[] = [];
  if (nomination.status === 'APPROVED') {
    lost.push('It was approved. Saving sends it back for review, and it needs to be approved again.');
  } else if (nomination.status === 'REJECTED') {
    lost.push('It was rejected. Saving resubmits it for review, and the rejection reason is cleared.');
  }
  if (nomination.attendanceConfirmation === 'CONFIRMED') {
    lost.push("The guest's attendance confirmation is cleared; confirm it again once it is re-approved.");
  }
  return lost;
}

/**
 * A failed edit, as the form says it.
 *
 * By the service's error types rather than its messages: a 403's message names the authorization
 * rule (it should not, CWE-209, and a reader should not be shown it either way), and a 409 has two
 * causes the BD handles the same way — reload and look again.
 */
export function editErrorMessage(e: unknown): string {
  const name = (e as { name?: string } | null)?.name;
  switch (name) {
    case 'ConflictException':
      return 'This nomination could not be saved: its event is no longer open for nomination, or it was changed meanwhile. Reload the page and try again.';
    case 'AccessDeniedException':
      return 'You cannot edit this nomination. Only the BD who submitted it can.';
    case 'ResourceNotFoundException':
      return 'This nomination, or its event, no longer exists.';
    default:
      return e instanceof Error ? e.message : String(e);
  }
}
